import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import axios from "axios";
import { useTranslation } from "react-i18next";
import config from "../config";

const PasswordResetPage = ({ accountType = "kinkeeper", loginPath = "/login" }) => {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const lastErrorKeyRef = useRef(null);

  useEffect(() => {
    if (!resendCooldown) return undefined;
    const timer = window.setInterval(() => setResendCooldown((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (lastErrorKeyRef.current) {
      setError(t(lastErrorKeyRef.current.key, lastErrorKeyRef.current.options));
    }
  }, [t]);

  const requestCode = async () => {
    const response = await axios.post(`${config.API_BASE_URL}/auth/forgot-password`, { email, accountType });
    setStep("code");
    setResendCooldown(60);
    setMessage(response.data.message);
  };

  const resendCode = async () => {
    if (resendCooldown || loading) return;
    setLoading(true); lastErrorKeyRef.current = null; setError(""); setMessage("");
    try { await requestCode(); }
    catch (requestError) {
      const backendMessage = requestError.response?.data?.error;
      if (backendMessage) {
        lastErrorKeyRef.current = null;
        setError(backendMessage);
      } else {
        lastErrorKeyRef.current = { key: "passwordReset.errors.resendFailed" };
        setError(t("passwordReset.errors.resendFailed"));
      }
    }
    finally { setLoading(false); }
  };

  const submit = async (event) => {
    event.preventDefault(); lastErrorKeyRef.current = null; setError(""); setMessage("");
    if (step === "password" && password !== confirmPassword) {
      lastErrorKeyRef.current = { key: "passwordReset.errors.passwordMismatch" };
      return setError(t("passwordReset.errors.passwordMismatch"));
    }
    setLoading(true);
    try {
      if (step === "email") {
        await requestCode();
      } else if (step === "code") {
        const response = await axios.post(`${config.API_BASE_URL}/auth/verify-reset-code`, { email, code, accountType });
        setResetToken(response.data.resetToken);
        setStep("password");
        setMessage(response.data.message);
      } else if (step === "password") {
        const response = await axios.post(`${config.API_BASE_URL}/auth/reset-password`, { resetToken, password, accountType });
        setPassword(""); setConfirmPassword(""); setResetToken(""); setStep("done"); setMessage(response.data.message);
      }
    } catch (requestError) {
      const backendMessage = requestError.response?.data?.error;
      if (backendMessage) {
        lastErrorKeyRef.current = null;
        setError(backendMessage);
      } else {
        lastErrorKeyRef.current = { key: "passwordReset.errors.recoveryFailed" };
        setError(t("passwordReset.errors.recoveryFailed"));
      }
    } finally { setLoading(false); }
  };

  const titles = {
    email: t("passwordReset.titles.email"),
    code: t("passwordReset.titles.code"),
    password: t("passwordReset.titles.password"),
    done: t("passwordReset.titles.done")
  };
  const descriptions = {
    email: t("passwordReset.descriptions.email"),
    code: t("passwordReset.descriptions.code", { email }),
    password: t("passwordReset.descriptions.password"),
    done: t("passwordReset.descriptions.done")
  };

  return <div className="page-shell"><section className="page-panel"><div className="page-content auth-layout">
    <div className="support-card"><div className="intro-badge">{t("passwordReset.badge")}</div><h1 className="page-title">{titles[step]}</h1><p className="page-subtitle">{descriptions[step]}</p></div>
    <form className="form-card" onSubmit={submit}>{message && <div className="success-banner">{message}</div>}{error && <div className="error-banner">{error}</div>}
      {step === "email" && <div className="form-field full"><label htmlFor="reset-email">{t("passwordReset.emailLabel")}</label><input id="reset-email" className="text-input" type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} /></div>}
      {step === "code" && <div className="form-field full"><label htmlFor="reset-code">{t("passwordReset.codeLabel")}</label><input id="reset-code" className="text-input" type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))} /><button className="simple-link" type="button" disabled={Boolean(resendCooldown) || loading} onClick={resendCode}>{resendCooldown ? t("passwordReset.resendCooldown", { seconds: resendCooldown }) : t("passwordReset.resendCode")}</button></div>}
      {step === "password" && <>
        <div className="form-field full"><label htmlFor="reset-password">{t("passwordReset.newPasswordLabel")}</label><div style={{position:"relative"}}><input id="reset-password" className="text-input" style={{paddingRight:"3.5rem"}} type={showPasswords ? "text" : "password"} autoComplete="new-password" minLength={8} required value={password} onChange={(event) => setPassword(event.target.value)} /><button type="button" style={passwordEyeButton} aria-label={showPasswords ? t("passwordReset.hidePasswords") : t("passwordReset.showPasswords")} onClick={() => setShowPasswords((shown) => !shown)}>{showPasswords ? <FaEyeSlash/> : <FaEye/>}</button></div></div>
        <div className="form-field full"><label htmlFor="reset-confirm">{t("passwordReset.confirmPasswordLabel")}</label><input id="reset-confirm" className="text-input" type={showPasswords ? "text" : "password"} autoComplete="new-password" minLength={8} required value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} /></div>
      </>}
      <div className="button-row">
        {step === "code" ? <button className="ghost-button" type="button" onClick={() => { setStep("email"); setCode(""); setMessage(""); lastErrorKeyRef.current = null; setError(""); }}>{t("passwordReset.changeEmail")}</button> : <button className="ghost-button" type="button" onClick={() => navigate(loginPath)}>{t("passwordReset.backToSignIn")}</button>}
        {step !== "done" && <button className="button" type="submit" disabled={loading}>{loading ? t("passwordReset.pleaseWait") : step === "email" ? t("passwordReset.sendCode") : step === "code" ? t("passwordReset.verifyCode") : t("passwordReset.resetPassword")}</button>}
      </div>
    </form></div></section></div>;
};
const passwordEyeButton = { position: "absolute", right: "1rem", top: "50%", transform: "translateY(-50%)", border: "none", background: "transparent", color: "#5f6c76", padding: 0 };
export default PasswordResetPage;
